import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useJoin, useJoinPreview } from '../api/hooks';
import { ApiError } from '../api/client';
import { Brand, SessionUserMenu } from '../components/Header';
import { ProfileDialog } from '../components/dialogs/ProfileDialog';
import { Icon } from '../components/Icon';
import { fmtShortDate } from '../lib/format';
import { useToast } from '../state/toasts';

/** `/join/:token`: an album invite link. A signed-in, active user joins the album as a collaborator. */
export function JoinPage() {
  const { token = '' } = useParams();
  const [profile, setProfile] = useState(false);
  const preview = useJoinPreview(token);
  const join = useJoin();
  const navigate = useNavigate();
  const toast = useToast();
  const p = preview.data;
  const go = (albumId: string) => navigate(`/studio/albums/${albumId}`);
  const accept = () =>
    join.mutate(token, {
      onSuccess: (r) => {
        toast({ icon: 'check', title: `You joined ${r.albumName}`, sub: 'You can upload and tag photos in this album.' });
        go(r.albumId);
      },
      onError: (e) => toast({ icon: 'info', title: 'Couldn’t join', sub: e.message }),
    });
  const code = preview.error instanceof ApiError ? preview.error.code : null;

  return (
    <>
      <header className="top">
        <div className="top-row studio-row solo">
          <div className="top-left">
            <Brand />
          </div>
          <div className="actions">
            <SessionUserMenu onProfile={() => setProfile(true)} />
          </div>
        </div>
        {profile && <ProfileDialog onClose={() => setProfile(false)} />}
      </header>
      <main className="wrap narrow-page">
        <div className="panel">
          {preview.isPending ? (
            <div className="sentinel">
              <div className="spinner" />
            </div>
          ) : preview.isError ? (
            <>
              <h3>
                {code === 'invite_expired'
                  ? 'This invite link has expired'
                  : code === 'access_removed'
                    ? 'Your access to this album was removed'
                    : 'This invite link doesn’t work'}
              </h3>
              <p className="help" style={{ margin: '0 0 14px' }}>
                {code === 'access_removed'
                  ? 'This link no longer lets you in. If you should still have access, ask the media team for a new invite link.'
                  : 'Ask the person who sent it for a new link.'}
              </p>
              <button type="button" className="btn" onClick={() => navigate('/')}>
                Back to photos
              </button>
            </>
          ) : (
            <>
              <div className="notice">
                <Icon name="users" />
                <span>
                  {p!.invitedBy} invited you to help with an album. The link works until {fmtShortDate(new Date(p!.expiresAt))}.
                </span>
              </div>
              <h3>{p!.albumName}</h3>
              <p className="help">
                As a collaborator you can upload photos to this album and tag them. You’ll find it in Studio. You can’t release photos or see other
                albums.
              </p>
              {p!.alreadyMember ? (
                <button type="button" className="btn primary" onClick={() => go(p!.albumId)}>
                  Open the album →
                </button>
              ) : (
                <button type="button" className="btn primary" disabled={join.isPending} onClick={accept}>
                  Join {p!.albumName}
                </button>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}
